import cx from 'classnames';
import { KeyboardEvent, useEffect, useRef } from 'react';
import { useMount } from 'react-use';

import { CheckboxFieldCheck } from '@app/components/CheckboxField';
import getPDFScrollableDOMElement from '@app/libs/reactPdfJs/utils/getPDFScrollableDOMElement';
import Position from '@app/libs/reactPdfJs/utils/position';

import './WritableCheckboxField.scss';

interface WritableCheckboxFieldProps {
  checked: boolean;
  id: string;
  initialRelativeOffsetX: number;
  initialRelativeOffsetY: number;
  isNeedFocus: boolean;
  relativeWidth: number;
  viewport: {
    height: number;
    width: number;
  };
  onCheckedChange: (fieldId: string, nextChecked: boolean) => void;
  onFocus: () => void;
  onPositionInitialize: (fieldId: string, left: number, top: number) => void;
  onHover: (hovered: boolean) => void;
}

const defaultProps = {
  checked: false,
  initialRelativeOffsetX: 0,
  initialRelativeOffsetY: 0,
  isNeedFocus: false,
  relativeWidth: 0,
  required: false,
  onCheckedChange(_fieldId: string, _nextChecked: boolean, _required: boolean) {},
  onFocus() {},
  onPositionInitialize(_fieldId: string, _left: number, _top: number) {},
};

function WritableCheckboxField({
  checked,
  id,
  initialRelativeOffsetX,
  initialRelativeOffsetY,
  isNeedFocus,
  relativeWidth,
  viewport,
  onCheckedChange,
  onFocus,
  onPositionInitialize,
  onHover,
}: WritableCheckboxFieldProps) {
  const divRef = useRef<HTMLDivElement>(null);

  const left = initialRelativeOffsetX * viewport.width;
  const length = relativeWidth * viewport.width;
  const opacity = checked ? 1 : 0.2;
  const top = initialRelativeOffsetY * viewport.height;

  useMount(() => {
    if (divRef.current) {
      const { top: divTop, left: divLeft } = Position.offset(divRef.current);
      const scrollHeight = getPDFScrollableDOMElement()?.scrollHeight || 0;
      const scrollWidth = getPDFScrollableDOMElement()?.scrollWidth || 0;
      onPositionInitialize(id, divLeft / scrollWidth, divTop / scrollHeight);
    }
  });

  useEffect(() => {
    if (isNeedFocus && divRef.current) {
      divRef.current.focus();
    }
  }, [isNeedFocus]);

  function handleCheckedChange() {
    const nextChecked = !checked;
    onCheckedChange(id, nextChecked);
  }

  function handleFocus() {
    onFocus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const SPACE_KEY = ' ';
    if (e.key === SPACE_KEY || e.key === 'Enter') {
      e.preventDefault();
      handleCheckedChange();
    }
  }

  return (
    <div // eslint-disable-line jsx-a11y/no-static-element-interactions
      className={cx('WritableCheckboxField', {
        'WritableCheckboxField--checked': checked,
      })}
      ref={divRef}
      style={{
        height: length,
        width: length,
        left,
        top,
      }}
      tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <div className="WritableCheckboxField__border" />
      <CheckboxFieldCheck opacity={opacity} onClick={handleCheckedChange} />
    </div>
  );
}

WritableCheckboxField.defaultProps = defaultProps;

export default WritableCheckboxField;
